import { NavLink, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAdminClient } from '../context/AdminClientContext';
import { useTheme } from '../context/ThemeContext';
import { useSimulations, usePopulations, useStimuli } from '../hooks/useClientScopedData';
import api from '../lib/api';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CommandPalette } from './CommandPalette';
import { 
  LayoutDashboard, 
  Users, 
  Zap, 
  Map, 
  PieChart,
  GitCompare,
  LogOut,
  Settings,
  Shield,
  Building2,
  BookOpen,
  Moon,
  Sun,
  Search
} from 'lucide-react';
import { cn } from '../lib/utils';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', group: 'main' },
];

const workItems = [
  { to: '/simulations', icon: Map, label: 'Simulations' },
  { to: '/populations', icon: Users, label: 'Populations' },
  { to: '/stimuli', icon: Zap, label: 'Stimuli' },
];

const analysisItems = [
  { to: '/analysis', icon: PieChart, label: 'Analysis' },
  { to: '/compare', icon: GitCompare, label: 'Compare' },
];

const bottomNavItems = [
  { to: '/guide', icon: BookOpen, label: 'Guide' },
  { to: '/settings', icon: Settings, label: 'Settings' },  // Now visible to all users
  { to: '/admin', icon: Shield, label: 'Admin Panel', adminOnly: true },
];

// SynPop Logo component matching the original design
const SynPopLogo = ({ className = "", size = "md" }) => {
  const sizes = {
    sm: "h-6",
    md: "h-10",
    lg: "h-12",
    xl: "h-14"
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <img 
        src="https://customer-assets.emergentagent.com/job_popforge/artifacts/9qv32c8p_Color%20logo%20-%20no%20background.png" 
        alt="SynPop Logo" 
        className={cn(sizes[size], "w-auto")}
      />
    </div>
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clientInfo, setClientInfo] = useState(null);
  const { selectedClientId, setSelectedClientId, allClients, activeClient } = useAdminClient();
  const { theme, toggleTheme, isDark } = useTheme();
  const { data: simulations } = useSimulations();
  const { data: populations } = usePopulations();
  const { data: stimuliData } = useStimuli();

  useEffect(() => {
    if (user && !user.is_admin && user.client_id) {
      api.get('/auth/client-info')
        .then(res => setClientInfo(res.data.client))
        .catch(() => {});
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Count badges for nav items
  const navCounts = {
    '/simulations': simulations?.length || 0,
    '/populations': populations?.length || 0,
    '/stimuli': stimuliData?.length || 0,
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Command Palette */}
      <CommandPalette 
        simulations={simulations || []} 
        populations={populations || []} 
        stimuli={stimuliData || []} 
      />
      
      {/* Sidebar - dark mode improved */}
      <aside className="w-64 bg-slate-50 dark:bg-[hsl(226,43%,8%)] border-r border-slate-200 dark:border-[hsl(224,20%,16%)] h-screen sticky top-0 flex flex-col">
        {/* Logo */}
        <div className="px-5 py-6 border-b border-slate-200/70 dark:border-slate-800/70">
          <SynPopLogo size="lg" />
          
          {/* Admin Client Switcher - #5 Pill style */}
          {user?.is_admin && allClients.length > 0 && (
            <div className="mt-4">
              <label className="text-[10px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 block">
                Viewing as
              </label>
              <Select value={selectedClientId || 'all'} onValueChange={(value) => setSelectedClientId(value === 'all' ? null : value)}>
                <SelectTrigger className="w-full text-sm h-9 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg shadow-sm" data-testid="admin-client-selector">
                  <SelectValue placeholder="Select client..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="flex items-center gap-2">
                      <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                      All Clients
                    </span>
                  </SelectItem>
                  {allClients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      <span className="flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                        {client.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(!selectedClientId) && (
                <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-1 px-0.5 italic">Viewing all clients</p>
              )}
            </div>
          )}
          
          {/* Regular User Client Display - #5 Pill style */}
          {clientInfo && !user?.is_admin && (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 shadow-sm">
              <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="truncate font-medium">{clientInfo.name}</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {/* Search shortcut button */}
          <button
            onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
            className="w-full flex items-center gap-3 px-3 py-2 mb-3 rounded-lg text-sm text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-600 dark:hover:text-slate-300 bg-white dark:bg-slate-800/50 transition-all duration-150"
            data-testid="search-shortcut-btn"
          >
            <Search className="w-4 h-4" strokeWidth={1.75} />
            <span className="flex-1 text-left">Search...</span>
            <kbd className="hidden sm:inline-flex text-[10px] font-medium text-slate-300 dark:text-slate-600 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700">
              ⌘K
            </kbd>
          </button>
          {/* Main - Dashboard */}
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative',
                  isActive
                    ? 'bg-primary/10 text-primary shadow-sm nav-active-item'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )
              }
            >
              <Icon className="w-5 h-5" strokeWidth={1.75} />
              {label}
            </NavLink>
          ))}
          
          {/* Spacer */}
          <div className="pt-3" />
          
          {/* Work Section - Simulations, Populations, Stimuli */}
          {workItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative',
                  isActive
                    ? 'bg-primary/10 text-primary shadow-sm nav-active-item'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )
              }
            >
              <Icon className="w-5 h-5" strokeWidth={1.75} />
              <span className="flex-1">{label}</span>
              {navCounts[to] > 0 && (
                <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {navCounts[to]}
                </span>
              )}
            </NavLink>
          ))}
          
          {/* Spacer */}
          <div className="pt-3" />
          
          {/* Analysis Section */}
          {analysisItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative',
                  isActive
                    ? 'bg-primary/10 text-primary shadow-sm nav-active-item'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )
              }
            >
              <Icon className="w-5 h-5" strokeWidth={1.75} />
              {label}
            </NavLink>
          ))}
          
          {/* Divider */}
          <div className="my-4 border-t border-border" />
          
          {/* Bottom nav items (Guide, Settings, Admin) */}
          {bottomNavItems.map(({ to, icon: Icon, label, adminOnly }) => {
            if (adminOnly && !user?.is_admin) return null;
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative',
                    isActive
                      ? 'bg-primary/10 text-primary shadow-sm nav-active-item'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  )
                }
              >
                <Icon className="w-5 h-5" strokeWidth={1.75} />
                {label}
              </NavLink>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-200/70 dark:border-[hsl(224,20%,16%)] bg-white/60 dark:bg-white/[0.02]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
              <span className="text-sm font-semibold text-white">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{user?.name}</p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>

          {/* Theme Toggle & Logout — separated, calmer chrome */}
          <div className="flex items-center gap-1.5 pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="flex-1 h-8 justify-center text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="theme-toggle-sidebar"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <Sun className="w-4 h-4" strokeWidth={1.75} />
              ) : (
                <Moon className="w-4 h-4" strokeWidth={1.75} />
              )}
            </Button>
            <span className="w-px h-4 bg-slate-200 dark:bg-slate-700" aria-hidden />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="flex-1 h-8 justify-center text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
              data-testid="logout-btn"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-white dark:bg-background noise-overlay">
        <div className="p-6 md:p-8 page-enter">
          {children}
        </div>
      </main>
    </div>
  );
};

export { SynPopLogo };
